/**
 * scrollIntoView that honours prefers-reduced-motion. The CSS reduced-motion
 * guard cannot reach a JS `scrollIntoView({ behavior: "smooth" })` call, so
 * route every smooth in-page jump through here: when the user has asked for
 * reduced motion the scroll snaps instead of animating.
 */
export function scrollIntoViewSafe(
  el: Element | null | undefined,
  options: ScrollIntoViewOptions = {},
): void {
  if (!el) return;
  const reduce =
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({
    ...options,
    behavior: reduce ? "auto" : (options.behavior ?? "auto"),
  });
}

/**
 * Briefly flash an element so the eye lands after a jump (a section jump, a
 * find-hit). Restarts cleanly if called again mid-flash. The flash is a CSS
 * animation, so the global prefers-reduced-motion block silences it; the class
 * is still added/removed so nothing is left in a stuck state.
 */
export function flash(el: Element | null | undefined): void {
  if (!el) return;
  el.classList.remove("nx-flash");
  // force a reflow so re-adding the class restarts the animation
  void (el as HTMLElement).offsetWidth;
  el.classList.add("nx-flash");
  window.setTimeout(() => el.classList.remove("nx-flash"), 1300);
}

/** Scroll to an element (reduced-motion aware) and flash it on arrival. */
export function jumpToElement(
  el: Element | null | undefined,
  options: ScrollIntoViewOptions = { behavior: "smooth", block: "start" },
): void {
  if (!el) return;
  scrollIntoViewSafe(el, options);
  flash(el);
}
